import { ElLoading, type ElLoadingService } from 'element-plus';
import axios from 'axios';
import { useCssVar } from '@vueuse/core';

import router from '@/router';

import { useUserStore } from '@/stores/user';

import { emptyStr2null } from '@/utils/util';

import {
    buildEncryptionHeaders,
    decryptResponseBody,
    encryptRequestBody,
    ensureSession,
    isEncryptedEnvelope,
    resetSession,
    shouldEncrypt
} from './encryption';

import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { EncryptedEnvelope } from '@/types/crypto';

class Request {
    // 实例
    instance: AxiosInstance;

    // 基础默认配置
    baseConfig: AxiosRequestConfig = {
        baseURL: '/',
        timeout: 30000,
        headers: {},
        showErrorMessage: true,
        errorReject: true,
        emptyToNull: false,
        // 屏蔽特定错误提示
        filterErrorCode: []
    };

    // 临时存储所有过期的接口列表，token刷新后遍历此列表重新调用
    requests: (() => void)[] = [];

    // 是否正在刷新token
    isRefreshing = false;

    //
    loadingInstance: null | ReturnType<typeof ElLoadingService> = null;

    // 构造函数
    constructor(config: AxiosRequestConfig) {
        this.instance = axios.create({
            ...this.baseConfig,
            ...config
        });

        /**
         * 请求拦截器
         */
        this.instance.interceptors.request.use(
            async (config: InternalAxiosRequestConfig) => {
                // loading效果展示
                if (config.loading) {
                    const background = useCssVar('--el-mask-color');
                    this.loadingInstance = ElLoading.service({
                        target: window.document.getElementById('globalLoadingEl') as HTMLElement,
                        lock: true,
                        text: '数据请求中···',
                        background
                    });
                }

                const token = window.localStorage.getItem('token');

                if (config.headers) {
                    config.headers['token'] = token;
                }

                /**
                 * URL上带有path参数处理
                 */
                if (config.url && config.url.match(/:[0-9a-z_]+/gi) && config.data) {
                    const url = config.url.replace(/:([0-9a-z_])+/gi, (key): string => {
                        const value = config.data[key.substring(1)];
                        delete config.data[key.substring(1)];
                        return value;
                    });
                    config.url = url;
                }

                /**
                 * 把参数中的空字符串转换成null
                 */
                if (config.emptyToNull && config.data) {
                    emptyStr2null(config.data);
                }

                /**
                 * 处理get参数
                 */
                if (config.method === 'get') {
                    config.params = { ...config.data };
                    if (config.headers) {
                        config.headers['Content-Type'] = '';
                    }
                }

                /**
                 * 应用层加密：非握手接口且启用加密时，自动建立会话并加密请求体
                 * multipart 上传（FormData）跳过加密：文件无法 JSON 序列化，
                 * 后端对白名单内的上传接口直接放行，响应也为明文
                 */
                const reqContentType = config.headers?.['Content-Type'] || config.headers?.['content-type'];
                const isMultipart =
                    (typeof FormData !== 'undefined' && config.data instanceof FormData) ||
                    (typeof reqContentType === 'string' && reqContentType.startsWith('multipart/'));
                if (config.headers && !isMultipart && shouldEncrypt(config.url)) {
                    await ensureSession();
                    Object.assign(config.headers, buildEncryptionHeaders());
                    if (config.method !== 'get' && config.data != null) {
                        // 保留明文，801 会话过期重试时需要重新加密
                        config.__plainData = config.data;
                        config.data = await encryptRequestBody(config.data);
                    }
                    config.__encrypted = true;
                }

                return config;
            },
            (error: any) => {
                return Promise.reject(error);
            }
        );

        /**
         * 响应拦截器（先解密，再处理业务状态）
         */
        this.instance.interceptors.response.use(
            async (response) => {
                const config = response.config;
                const userStore = useUserStore();

                // 关闭loading效果
                if (this.loadingInstance) {
                    this.loadingInstance.close();
                }

                /**
                 * 应用层加密：响应体为加密信封时先解密，再交给后续业务逻辑
                 */
                if (config.__encrypted && isEncryptedEnvelope(response.data)) {
                    try {
                        response.data = await decryptResponseBody(response.data as EncryptedEnvelope);
                        // 会话密钥失效时清理本地会话，后续请求自动重握手
                        if (
                            response.data &&
                            typeof response.data === 'object' &&
                            String((response.data as any).state) === '801'
                        ) {
                            resetSession();
                        }
                    } catch (err) {
                        return Promise.reject(new Error('响应解密失败'));
                    }
                }

                /**
                 * 加密 Filter 层的错误响应（如 801 会话密钥过期）是明文 JSON，不带加密信封，
                 * 上面的解密分支识别不到；这里检测后重置会话并重试一次。
                 * 重试会重新经过请求拦截器：覆盖加密头，并用 __plainData 重新加密请求体
                 */
                if (
                    config.__encrypted &&
                    !config.__retriedAfter801 &&
                    response.data &&
                    typeof response.data === 'object' &&
                    String((response.data as any).state) === '801'
                ) {
                    resetSession();
                    config.__retriedAfter801 = true;
                    if (config.__plainData !== undefined) {
                        config.data = config.__plainData;
                    }
                    await ensureSession();
                    return this.instance.request(config);
                }

                if (config.responseType === 'blob') {
                    return {
                        data: response.data,
                        resp: { resp_code: 'CP0000', resp_msg: '请求成功!', timestamp: new Date().getTime() }
                    };
                } else {
                    /**
                     * 请求成功
                     */
                    if (response.data.state * 1 === 1) {
                        response.data.state = response.data.state * 1;
                        return response.data;
                    } else if (response.data.state * 1 === 888888) {
                        // token 失效，执行登出并跳转到登录页
                        userStore.logout(false);
                        router.push('/login');
                        return Promise.reject(new Error('Token 已失效，请重新登录'));
                    } else {
                        if (config.showErrorMessage) {
                            ElMessage({
                                showClose: false,
                                message: `${response.data.msg}`,
                                type: 'error'
                            });
                        }

                        if (config.errorReject) {
                            return Promise.reject(response.data);
                        } else {
                            return response.data;
                        }
                    }
                }
            },
            (error) => {
                if (error.response) {
                    enum status {
                        '请求错误' = 400,
                        '未授权，请重新登录' = 401,
                        '拒绝访问' = 403,
                        '未找到' = 404,
                        '请求超时' = 408,
                        '服务器错误' = 500,
                        '服务未实现' = 501,
                        '网络错误' = 502,
                        '服务不可用' = 503,
                        '网络超时' = 504,
                        'HTTP版本不受支持' = 505
                    }

                    ElMessage({
                        showClose: true,
                        message: `${status[error.response.status]}(${error.response.status})，请检查网络或联系管理员！`,
                        type: 'error'
                    });

                    return Promise.reject(error.response);
                } else {
                    ElMessage({
                        showClose: true,
                        message: `${error.message}，请检查网络或联系管理员！`,
                        type: 'error'
                    });
                    return Promise.reject(error.message);
                }
            }
        );
    }

    /**
     * http 方法
     */
    public http<T = any>(config: AxiosRequestConfig): Promise<Common.ResponseResult<T>> {
        return this.instance.request(config);
    }
}

export default Request;
